import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/react';
import { history } from '@tiptap/pm/history';

import { TDocument, TLockedState } from '../types';
import { TTemplateBlobInfo } from '../types/documents.types';
import { TPermissions } from '@modules/access';
import { useEditDraft, useGetContent, useGetBoundContent } from '../api';
import { loadDocument } from '../largeDocument';
import insertSpaceIntoEmptyTags from '../helpers/insertSpaceIntoEmptyTags.helper';
import { isVersionBound } from '../helpers/isVersionBound.helper';
import { isBoundDocument, createTemplateLockingPlugin } from '../tiptap/extensions/TemplateLocking';
import hasUnsupportedMarkup from '../helpers/hasUnsupportedMarkup.helper';
import { useSearchParams } from '@shared/hooks';
import { ToastContext } from '@shared/contexts';
import { GENERIC_ERROR_MESSAGE } from '@shared/i18n';
import { useLog } from '@modules/users';

type TUseHandleRequestDraftParams = {
  doc: TDocument;
  permissions: TPermissions;
  editor: Editor | null;
  requestEdit: boolean;
  setLockedState: Dispatch<SetStateAction<TLockedState | undefined>>;
  connect: () => void;
  templateBlobInfo?: TTemplateBlobInfo;
};

type TUseHandleRequestDraftReturn = {
  wasViewMode: boolean;
};

/**
 * Handles the "Edit draft" flow: navigation guards → create/fetch draft → load content → connect WS.
 *
 * `enterEditMode` is the shared concern between bound and regular documents:
 *   - clears undo/redo history (avoids the Mod-z bug after reconnect)
 *   - opens the WebSocket connection so the editor becomes editable
 *
 * Bound documents (no blobName on draft):
 *   Fetches template HTML + draft field values, composes them, and sets the editor content
 *   before opening the WebSocket - same pattern as regular docs. This ensures the editor
 *   always starts from the DRAFT's current state, not from a previously cached version.
 *
 * Regular documents:
 *   Fetch the latest draft HTML blob, set it as editor content, then call `enterEditMode` as callback.
 */
const useHandleRequestDraft = ({
  doc,
  permissions,
  editor,
  requestEdit,
  setLockedState,
  connect,
  templateBlobInfo,
}: TUseHandleRequestDraftParams): TUseHandleRequestDraftReturn => {
  const { change } = useSearchParams();
  const { show } = useContext(ToastContext);
  const getBlob = useGetContent();
  const getBoundContent = useGetBoundContent();
  const editDraftMutation = useEditDraft();
  const logMutation = useLog();
  const [wasViewMode, setWasViewMode] = useState(false);

  // Use a ref so enterEditMode always captures the latest editor instance,
  // even if editor wasn't ready at the exact moment requestEdit became true.
  const refEditor = useRef(editor);
  refEditor.current = editor;

  useEffect(() => {
    // Shared: clear undo/redo history and open the WebSocket connection.
    const enterEditMode = () => {
      if (!refEditor.current) return;
      refEditor.current.unregisterPlugin('history');
      refEditor.current.registerPlugin(history());
      connect();
    };

    // Catches every way into edit mode - button, auto-edit, and a bare `?view=edit` URL restored
    // by browser back/forward - because they all end up here before the WebSocket connects.
    const rejectUnsupportedContent = () => {
      show('error', GENERIC_ERROR_MESSAGE);
      logMutation.mutate({ message: `UNSUPPORTED_MARKUP documentId=${doc._id} versionId=draft` });
      change('view');
    };

    const handleRequestDraft = async () => {
      if (!requestEdit) {
        setWasViewMode(true);
        return;
      }
      if (!wasViewMode) {
        change('view');
        return;
      }

      try {
        const res = await editDraftMutation.mutateAsync({ id: doc._id });

        if (!res.draft) {
          setLockedState({ isOpen: true, lockedUserId: res.lockedUserId || null });
          change('view');
          return;
        }

        if (isVersionBound(res.draft)) {
          // Bound document: explicitly fetch and compose the draft's current content before
          // entering edit mode. This mirrors the regular-doc pattern and fixes two scenarios:
          // 1. User was viewing an older version - refCache holds that version's HTML, not draft's.
          // 2. Slow initial load - content may not have been set into the editor yet.
          // Without this fetch, the editor starts with wrong/stale content which, if saved,
          // would overwrite the draft's actual field values.
          const fieldValuesBlobName = res.draft.templateSource?.fieldValuesBlobName;
          const draftTemplateVersionId = res.draft.templateSource?.versionId;
          const resolvedBlobName = draftTemplateVersionId
            ? templateBlobInfo?.versionBlobNames?.[draftTemplateVersionId]
            : undefined;
          if (templateBlobInfo && fieldValuesBlobName && resolvedBlobName) {
            const composedHtml = await getBoundContent.mutateAsync({
              templateBlobInfo,
              templateBlobName: resolvedBlobName,
              token: permissions.token,
              containerName: doc._id,
              fieldValuesBlobName,
            });
            if (hasUnsupportedMarkup(composedHtml)) return rejectUnsupportedContent();
            if (!refEditor.current) return;
            if (!isBoundDocument(refEditor.current.state)) {
              refEditor.current.registerPlugin(
                createTemplateLockingPlugin(refEditor.current.schema, refEditor.current)
              );
            }
            loadDocument({
              editor: refEditor.current,
              doc,
              version: res.draft,
              html: insertSpaceIntoEmptyTags(composedHtml),
              onLoaded: enterEditMode,
            });
          } else {
            // templateBlobInfo unavailable - cannot compose bound doc content before entering edit
            // mode. Entering without content would risk overwriting field values with empty state
            // on auto-save. Redirect to view instead (same pattern as the catch block below).
            console.error('[RequestDraft] templateBlobInfo missing for bound doc - staying in view mode');
            change('view');
          }
          return;
        }

        // Regular document: fetch latest draft HTML and load it into the editor.
        if (!res.draft.blobName) {
          console.error('[RequestDraft] Regular doc draft missing blobName');
          change('view');
          return;
        }
        const newContent = await getBlob.mutateAsync({
          token: permissions.token,
          containerName: doc._id,
          blobName: res.draft.blobName,
        });

        if (hasUnsupportedMarkup(newContent)) return rejectUnsupportedContent();
        if (!refEditor.current) return;

        loadDocument({
          editor: refEditor.current,
          doc,
          version: res.draft,
          html: insertSpaceIntoEmptyTags(newContent),
          onLoaded: enterEditMode,
        });
      } catch (e) {
        console.error('[RequestDraft] error - redirecting to latest version', e);
        change('view');
      }
    };

    handleRequestDraft();
  }, [requestEdit]);

  return { wasViewMode };
};

export default useHandleRequestDraft;
